import React, { useCallback, useState } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import { UseFormRegister, FieldValues, Path } from 'react-hook-form';

interface FormInputProps<TFieldValues extends FieldValues> {
    name: Path<TFieldValues>;
    label: string;
    type?: React.HTMLInputTypeAttribute;
    register: UseFormRegister<TFieldValues>;
    error?: boolean;
    helperText?: string;
    isDisabled?: boolean;
    size?: 'large' | 'small';
    testId?: string;
}

const FormInput = <TFieldValues extends FieldValues>({
    name,
    label,
    type = 'text',
    register,
    error = false,
    helperText = '',
    isDisabled = false,
    size = 'small',
    testId,
}: FormInputProps<TFieldValues>) => {
    const [showPassword, setShowPassword] = useState(false);

    const handleTogglePassword = () => {
        setShowPassword(!showPassword);
    };

    const handleFocus = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            if (!isDisabled) {
                const length = event.target.value.length;
                event.target.setSelectionRange(length, length);
            }
        },
        [isDisabled]
    );

    const endAdornment =
        type === 'password' ? (
            <InputAdornment position='end'>
                <IconButton
                    onClick={handleTogglePassword}
                    edge='end'
                    disabled={isDisabled}
                    sx={{ color: error ? 'common.system.error.main' : 'common.grey1' }}
                >
                    {!showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                </IconButton>
            </InputAdornment>
        ) : null;

    return (
        <TextField
            {...register(name)}
            type={showPassword ? 'text' : type}
            label={label}
            error={error}
            helperText={helperText}
            data-testid={testId ? testId : 'form-input'}
            variant='outlined'
            fullWidth
            disabled={isDisabled}
            InputLabelProps={{
                style: {
                    color: 'common.grey1',
                    fontSize: size === 'small' ? '14px' : '16px',
                },
                sx: {
                    '&.Mui-focused': {
                        color: error ? 'common.system.error.main' : 'common.grey1',
                    },
                },
            }}
            InputProps={{
                onFocus: handleFocus,
                endAdornment: endAdornment,
            }}
            FormHelperTextProps={{
                style: {
                    color: error ? 'common.system.error.main' : 'common.grey2',
                },
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    height: '48px',
                    '& .MuiOutlinedInput-input': {
                        fontSize: '14px',
                    },
                    '& fieldset': {
                        borderColor: 'common.grey2',
                        borderWidth: '1px',
                        top: '-11px',
                    },
                    '& fieldset legend': {
                        height: 'auto !important',
                        fontSize: size === 'small' ? '0.65em' : '0.75em',
                    },
                    '&:hover fieldset': {
                        borderColor: isDisabled ? 'common.grey2' : 'common.black',
                    },
                    '&.Mui-focused fieldset': {
                        borderWidth: '1px',
                        borderColor: 'common.primary',
                    },
                    '&.Mui-error fieldset': {
                        borderColor: 'common.system.error.main',
                    },
                },
                '& .MuiFormHelperText-root.Mui-error': {
                    color: 'common.system.error.main',
                },
            }}
        />
    );
};

export default FormInput;
